import React, { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Modal } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import SignatureCanvas from "react-signature-canvas";

import api, { getFileUrl } from "../../../api";
import FilePreview from "../Common/FilePreview";
import swal from "sweetalert";

const getDeliveryItemKey = (item, index) => {
  if (item && item._id) return String(item._id);
  return `item_${index}`;
};

const MIREdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const projectFromUrl = searchParams.get("project");
  const workOrderFromUrl = searchParams.get("workOrder");
  const sigPadRef = useRef(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signatureFile, setSignatureFile] = useState(null);
  const [existingSignature, setExistingSignature] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [workOrders, setWorkOrders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [deliveryItems, setDeliveryItems] = useState([]);

  const [form, setForm] = useState({
    mirNumber: "",
    workOrder: "",
    deliveryReference: "",
    items: [],
    inspectionReport: null,
    inspectionDate: new Date().toISOString().split("T")[0],
    approvalStatus: "Pending",
    clientApproval: null,
    remarks: "",
  });

  // LOAD INITIAL DATA
  useEffect(() => {
    fetchWorkOrders();
    fetchDeliveries();
  }, []);

  useEffect(() => {
    if (deliveries.length > 0 && workOrders.length > 0) {
      fetchMIR();
    }
  }, [deliveries, workOrders]);

  // ---------------- FETCH WORK ORDERS ----------------
  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders?limit=1000");
      if (res.data.success) setWorkOrders(res.data.data);
    } catch {
      swal("Error", "Failed to load projects", "error");
    }
  };

  // ---------------- FETCH DELIVERIES ----------------
  const fetchDeliveries = async () => {
    try {
      const res = await api.get("/deliveries?limit=1000");
      if (res.data.success) setDeliveries(res.data.data);
    } catch {
      swal("Error", "Failed to load deliveries", "error");
    }
  };

  // ---------------- FETCH MIR ----------------
  const fetchMIR = async () => {
    try {
      setFetching(true);

      const res = await api.get(`/mir/${id}`);
      if (!res.data.success) return;

      const mir = res.data.data;

      let itemsArray = [];

      if (mir.items && mir.items.length > 0) {
        itemsArray = mir.items.map((it) => ({
          deliveryItemId: it.deliveryItemId || "",
          materialName: it.materialName,
          quantityInspected: it.quantityInspected,
          unit: it.unit,
          maxDelivered: it.quantityDelivered || it.quantityInspected || 0,
          remark: it.remark || it.remarks || "",
          isNonBillable: it.isNonBillable || false,
          subItems: it.subItems || [],
        }));
      } else {
        itemsArray = [
          {
            deliveryItemId: "",
            materialName: mir.materialName,
            quantityInspected: mir.quantityInspected,
            unit: mir.unit,
            maxDelivered: mir.quantityInspected,
            isNonBillable: mir.isNonBillable || false,
          },
        ];
      }

      const projectDeliveries = deliveries.filter((d) => {
        const woId = d.workOrder?._id || d.workOrder;
        return String(woId) === String(mir.workOrder?._id);
      });

      setFilteredDeliveries(projectDeliveries);

      const selectedDelivery = deliveries.find(
        (d) => d._id === mir.deliveryReference?._id,
      );
      const delItems = selectedDelivery?.items || [];
      setDeliveryItems(delItems);

      const usedDelItemKeys = new Set();
      const mappedItems = itemsArray.map((it) => {
        let matchedKey = it.deliveryItemId ? String(it.deliveryItemId) : "";

        if (!matchedKey && delItems.length > 0) {
          const found = delItems.find((di, dIdx) => {
            const key = getDeliveryItemKey(di, dIdx);
            return di.itemName === it.materialName && !usedDelItemKeys.has(key);
          });
          if (found) {
            matchedKey = getDeliveryItemKey(found, delItems.indexOf(found));
          }
        }

        if (matchedKey) {
          usedDelItemKeys.add(matchedKey);
        }

        return {
          ...it,
          deliveryItemId: matchedKey,
        };
      });

      setForm({
        mirNumber: mir.mirNumber,
        workOrder: mir.workOrder?._id || "",

        deliveryReference: mir.deliveryReference?._id || "",
        items: mappedItems,
        inspectionReport: mir.inspectionReport,
        inspectionDate: mir.inspectionDate?.split("T")[0],
        approvalStatus: mir.approvalStatus,
        clientApproval: mir.clientApproval,
        remarks: mir.remarks || "",
      });
      setExistingSignature(mir.signature || null);
    } catch {
      swal("Error", "Failed to load MIR", "error");
      navigate(
        workOrderFromUrl
          ? `/mir?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
          : "/mir",
      );
    } finally {
      setFetching(false);
    }
  };
  const isApproved = form.approvalStatus === "Approved";

  // ---------------- HANDLE CHANGE ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    // WORK ORDER CHANGE
    if (name === "workOrder") {
      const filtered = deliveries.filter((d) => {
        const woId = d.workOrder?._id || d.workOrder;
        return String(woId) === String(value);
      });

      setFilteredDeliveries(filtered);
      setDeliveryItems([]);

      setForm((p) => ({
        ...p,
        workOrder: value,
        deliveryReference: "",
        items: [],
      }));
      return;
    }

    // DELIVERY CHANGE
    if (name === "deliveryReference") {
      const found = deliveries.find((d) => d._id === value);
      setDeliveryItems(found?.items || []);

      setForm((p) => ({
        ...p,
        deliveryReference: value,
        items: [],
      }));
      return;
    }

    // NORMAL INPUT
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------- ITEM HANDLER ----------------
  const handleSubItemChange = (itemIndex, subIndex, field, value) => {
    const updated = [...form.items];
    const currentItem = { ...updated[itemIndex] };
    const subItems = [...(currentItem.subItems || [])];
    let finalValue = value;

    if (field === "quantityInspected" && finalValue !== "") {
      const numVal = parseFloat(finalValue);
      const maxVal = parseFloat(subItems[subIndex]?.quantityDelivered || subItems[subIndex]?.quantity || 0);
      if (!isNaN(numVal) && !isNaN(maxVal) && numVal > maxVal) {
        finalValue = maxVal;
      }
    }

    const updatedSub = {
      ...subItems[subIndex],
      [field]: finalValue,
    };

    subItems[subIndex] = updatedSub;
    currentItem.subItems = subItems;

    // Pro-rata parent quantityInspected from sub-items
    const totalSubDelivered = subItems.reduce(
      (sum, s) => sum + (parseFloat(s.quantityDelivered || s.quantity) || 0),
      0
    );
    const currentTotalInspected = subItems.reduce(
      (sum, s) => sum + (parseFloat(s.quantityInspected) || 0),
      0
    );
    const parentDeliveredQty = parseFloat(currentItem.maxDelivered || currentItem.quantityDelivered) || 0;

    if (parentDeliveredQty > 0 && totalSubDelivered > 0) {
      const newParentInspected = currentTotalInspected === 0
        ? 0
        : parseFloat(((currentTotalInspected / totalSubDelivered) * parentDeliveredQty).toFixed(4));
      currentItem.quantityInspected = newParentInspected;
    }

    updated[itemIndex] = currentItem;
    setForm((prev) => ({ ...prev, items: updated }));
  };

  const handleItemChange = (idx, field, value) => {
    const updated = [...form.items];

    if (field === "deliveryItemId") {
      const exists = form.items.some(
        (it, i) => it.deliveryItemId === value && i !== idx,
      );

      if (exists) {
        swal("Warning", "This material is already selected", "warning");
        return;
      }

      const item = deliveryItems.find(
        (i, dIdx) => getDeliveryItemKey(i, dIdx) === value
      );

      if (item) {
        updated[idx] = {
          deliveryItemId: value,
          materialName: item.itemName,
          unit: item.unit,
          quantityInspected: item.quantityDelivered,
          maxDelivered: item.quantityDelivered,
          remark: item.remark || "",
          isNonBillable: item.isNonBillable || false,
          subItems: item.subItems || [],
        };
      } else {
        updated[idx] = {
          deliveryItemId: "",
          materialName: "",
          unit: "",
          quantityInspected: "",
          maxDelivered: 0,
          remark: "",
          isNonBillable: false,
        };
      }
    }

    if (field === "remark") {
      updated[idx].remark = value;
    }

    if (field === "quantityInspected") {
      const max = updated[idx].maxDelivered;

      if (Number(value) > Number(max)) {
        swal("Warning", `Cannot exceed delivered qty (${max})`, "warning");
        return;
      }

      updated[idx].quantityInspected = value;
    }

    setForm((prev) => ({ ...prev, items: updated }));
  };

  const addItem = () => {
    if (!form.deliveryReference)
      return swal("Warning", "Select delivery first", "warning");

    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          deliveryItemId: "",
          materialName: "",
          quantityInspected: "",
          unit: "",
          maxDelivered: 0,
          isNonBillable: false,
        },
      ],
    }));
  };

  const removeItem = (i) => {
    if (form.items.length === 1) {
      swal("Warning", "At least one item required", "warning");
      return;
    }

    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== i),
    }));
  };

  // ---------------- FILE UPLOAD ----------------
  const handleFile = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX = 20 * 1024 * 1024; // 20MB
    if (file.size > MAX) {
      swal("Warning", "File must be less than 20MB", "warning");
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: file, // store FILE object
    }));
  };
  const clearSignature = () => {
    sigPadRef.current.clear();
    setSignatureFile(null);
  };

  const saveSignature = () => {
    if (sigPadRef.current.isEmpty()) {
      return swal("Error", "Please sign before saving", "error");
    }

    const dataURL = sigPadRef.current.toDataURL("image/png");
    const blob = dataURLtoBlob(dataURL);

    const file = new File([blob], "mir-signature.png", {
      type: "image/png",
    });

    setSignatureFile(file);
    setShowSignModal(false);

    swal("Success", "Signature updated", "success");
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const fd = new FormData();
      fd.append("workOrder", form.workOrder);
      fd.append("deliveryReference", form.deliveryReference);
      fd.append("inspectionDate", form.inspectionDate);
      fd.append("remarks", form.remarks);
      fd.append("items", JSON.stringify(form.items));

      if (form.inspectionReport instanceof File) {
        fd.append("inspectionReport", form.inspectionReport);
      }
      if (form.clientApproval instanceof File) {
        fd.append("clientApproval", form.clientApproval);
      }
      if (signatureFile instanceof File) {
        fd.append("signature", signatureFile);
      }

      setLoading(true);
      const res = await api.patch(`/mir/${id}`, fd);

      if (res.data.success) {
        swal("Success", "MIR updated successfully!", "success");

        const filterQuery = [
          projectFromUrl ? `project=${projectFromUrl}` : "",
          workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
        ].filter(Boolean).join("&");

        navigate(filterQuery ? `/mir?${filterQuery}` : "/mir");
      }
    } catch (err) {
      swal(
        "Error",
        err.response?.data?.message || "Failed to update MIR",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------- UI ----------------
  if (fetching) {
    return (
      <Fragment>
        <PageTitle activeMenu="Edit MIR" motherMenu="Quality" />
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <p>Loading...</p>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageTitle activeMenu="Edit MIR" motherMenu="Quality" />

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <Card.Title>Edit Material Inspection Report</Card.Title>
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    workOrderFromUrl
                      ? `/mir?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                      : "/mir",
                  )
                }
              >
                Back
              </Button>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* MIR NUMBER */}
                <Form.Group className="mb-3">
                  <Form.Label>MIR Number</Form.Label>
                  <Form.Control value={form.mirNumber} readOnly />
                </Form.Group>

                {/* PROJECT & DELIVERY */}
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Work Order</Form.Label>
                      <Form.Select
                        name="workOrder"
                        value={form.workOrder}
                        onChange={handleChange}
                      >
                        <option value="">Select Work Order</option>

                        {workOrders.map((wo) => {
                          const woNo = wo.woNumber || wo.woNo || "WO";
                          const projectName =
                            wo.projectName ||
                            wo.project?.projectName ||
                            wo.project?.name ||
                            "";

                          return (
                            <option key={wo._id} value={wo._id}>
                              {woNo}{wo.label ? ` – ${wo.label}` : ""} {projectName && `— ${projectName}`}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Delivery</Form.Label>
                      <Form.Select
                        name="deliveryReference"
                        value={form.deliveryReference}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {filteredDeliveries.map((d) => (
                          <option key={d._id} value={d._id}>
                            {d.deliveryNumber} — {d.challanNumber}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* MATERIAL ITEMS */}
                <div className="mt-4">
                  <h5>Materials</h5>

                  {form.items.map((item, idx) => (
                    <Card key={idx} className="p-3 mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <h6>Item {idx + 1}</h6>
                        {form.items.length > 1 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeItem(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <Row>
                        <Col md={5}>
                          <Form.Group>
                            <Form.Label className="d-flex justify-content-between">
                              <span>Material</span>
                              {item.isNonBillable && (
                                <span
                                  className="badge badge-warning py-1 px-2"
                                  style={{ fontSize: "10px" }}
                                >
                                  Non-Billable
                                </span>
                              )}
                            </Form.Label>
                            <Form.Select
                              disabled={isApproved}
                              value={item.deliveryItemId || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "deliveryItemId",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select</option>

                              {deliveryItems.map((di, i) => {
                                const key = getDeliveryItemKey(di, i);
                                return (
                                  <option
                                    key={key}
                                    value={key}
                                    disabled={form.items.some(
                                      (x, xIdx) =>
                                        x.deliveryItemId === key && xIdx !== idx,
                                    )}
                                  >
                                    {di.itemName} (Delivered: {di.quantityDelivered}{di.unit ? ` ${di.unit}` : ""}{di.poNumber ? ` | PO: ${di.poNumber}` : ""}{di.remark ? ` | ${di.remark}` : ""})
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>Inspected Qty</Form.Label>
                            <Form.Control
                              type="number"
                              value={item.quantityInspected}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "quantityInspected",
                                  e.target.value,
                                )
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>Unit</Form.Label>
                            <Form.Control value={item.unit} readOnly />
                          </Form.Group>
                        </Col>

                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Item Remark</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Remark"
                              disabled={isApproved}
                              value={item.remark || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "remark",
                                  e.target.value,
                                )
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                  {item.subItems && item.subItems.length > 0 && (
                    <div className="mt-3 p-2 bg-light rounded border">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-primary fw-bold">
                          <i className="fa fa-list-ul me-1"></i> Sub-Items Breakdown (Enter inspected quantity for sub-items):
                        </small>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered bg-white mb-0" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "35%" }}>Sub-Item Name</th>
                              <th style={{ width: "15%" }} className="text-center">Delivered Qty</th>
                              <th style={{ width: "20%" }} className="text-center">Inspected Qty <span className="text-danger">*</span></th>
                              <th style={{ width: "15%" }} className="text-center">UOM</th>
                              <th style={{ width: "15%" }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.subItems.map((sub, sIdx) => (
                              <tr key={sIdx}>
                                <td className="align-middle"><strong>{sub.name}</strong></td>
                                <td className="align-middle text-center font-w600 text-muted">{sub.quantityDelivered !== undefined ? sub.quantityDelivered : sub.quantity}</td>
                                <td className="align-middle">
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={sub.quantityDelivered !== undefined ? sub.quantityDelivered : sub.quantity}
                                    size="sm"
                                    placeholder="0.00"
                                    disabled={isApproved}
                                    value={sub.quantityInspected !== undefined ? sub.quantityInspected : (sub.quantityDelivered !== undefined ? sub.quantityDelivered : (sub.quantity ?? ""))}
                                    onChange={(e) => handleSubItemChange(idx, sIdx, "quantityInspected", e.target.value)}
                                    style={{ fontSize: "0.82rem" }}
                                    required
                                  />
                                </td>
                                <td className="align-middle text-center">
                                  <span className="badge bg-light text-dark border px-2 py-1">
                                    {sub.unit || item.unit || "—"}
                                  </span>
                                </td>
                                <td className="align-middle text-muted">{sub.remarks || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
</Card>
                  ))}

                  <Button variant="success" size="sm" onClick={addItem}>
                    + Add Material
                  </Button>
                </div>

            {/* DOCUMENTS */}
            <div className="mt-4">
              <h5>Documents</h5>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Inspection Report *</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFile(e, "inspectionReport")}
                    />
                    <FilePreview
                      file={form.inspectionReport}
                      onRemove={() => setForm(prev => ({ ...prev, inspectionReport: null }))}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Client Approval (Optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFile(e, "clientApproval")}
                    />
                    <FilePreview
                      file={form.clientApproval}
                      onRemove={() => setForm(prev => ({ ...prev, clientApproval: null }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* REMARKS */}
            <Form.Group className="mt-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.remarks}
                onChange={(e) =>
                  setForm((p) => ({ ...p, remarks: e.target.value }))
                }
              />
            </Form.Group>
            {/* SIGNATURE SECTION */}
            <div className="mt-4">
              <h5>Signature</h5>

              {existingSignature && !signatureFile && (
                <div className="mb-2">
                  <small className="text-muted d-block">
                    Existing Signature
                  </small>
                  <img
                    src={getFileUrl(existingSignature.fileUrl || existingSignature.filePath)}
                    alt="Signature"
                    style={{ height: "80px", border: "1px solid #ccc" }}
                  />
                </div>
              )}

              {signatureFile && (
                <small className="text-success d-block mb-2">
                  New signature added
                </small>
              )}

              <Button
                type="button"
                variant="warning"
                onClick={() => {
                  setShowSignModal(true);
                  setTimeout(() => sigPadRef.current?.clear(), 0);
                }}
              >
                {existingSignature ? "Re-sign MIR" : "Sign MIR"}
              </Button>
            </div>

            {/* BUTTONS */}
            <div className="d-flex gap-2 mt-3">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Updating..." : "Update MIR"}
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  navigate(
                    workOrderFromUrl
                      ? `/mir?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                      : "/mir",
                  )
                }
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Col>
 </Row >
  <Modal
    show={showSignModal}
    onHide={() => setShowSignModal(false)}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>Sign MIR</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <div
        style={{
          width: "100%",
          maxWidth: "600px",
          height: "200px",
          border: "2px dashed #999",
          margin: "0 auto",
        }}
      >
        <SignatureCanvas
          ref={sigPadRef}
          penColor="black"
          canvasProps={{
            width: 600,
            height: 200,
            style: { width: "100%", height: "200px" },
          }}
        />
      </div>
    </Modal.Body>

    <Modal.Footer>
      <Button variant="secondary" onClick={clearSignature}>
        Clear
      </Button>
      <Button variant="success" onClick={saveSignature}>
        Save Signature
      </Button>
    </Modal.Footer>
  </Modal>
 </Fragment >
 );
};

export default MIREdit;
